import React from 'react';
import { useLocation } from 'react-router';
import Login from 'pages/Login';
import Dashboard from 'pages/Dashboard';
import TemporaryRootRedirect from 'pages/TemporaryRootRedirect';
import Opportunities from 'pages/Opportunities/Opportunities';
import PreviewOpportunities from 'pages/Preview/PreviewOpportunities';
import Subscription from 'pages/Subscription';
import Unconfirmed from 'pages/Unconfirmed/Unconfirmed';
import Support from 'pages/Support/Support';
import NotFound from 'pages/NotFound/NotFound';
import Welcome from 'pages/Welcome/Welcome';
import Settings from 'pages/Settings/Settings';
import { NavBar } from 'components';
import { Paper, ErrorBoundary, styled } from 'bonfire-kindling';
import AppProviders from 'providers';
import { useApp } from 'providers/AppProvider';
import { useAuth } from 'providers/AuthProvider';
import { Router, Route, Switch, Redirect } from 'react-router-dom';
import { createBrowserHistory } from 'history';
import { useAnalytics, useBounceUrl } from 'helpers/hooks';
import { checkMarketingiFrameExists } from 'helpers/marketing';
import ConfirmAccount from 'pages/Login/ConfirmAccount';
import CompleteSetup from 'pages/Login/CompleteSetup';
import GetStarted from 'pages/Login/GetStarted';
import Register from 'pages/Login/Register';
import { TrackJS } from 'trackjs';

// Create a history object for listening to route changes
const history = createBrowserHistory();

const PageLayout = styled(Paper)(({ theme, hasNav }) => ({
  display: 'flex',
  minHeight: '100vh',
  paddingTop: hasNav ? theme.mixins.toolbar.minHeight + 2 : 0,
}));

const onCatch = error => {
  if (error.response?.status === 401) {
    // -- Unauthenticated user. Redirect to the login page.
    window.location.href = `/login?bounceUrl=${window.location.href}`;
    return;
  }
  TrackJS.track(error);
  // If bounceUrl is specified and has invalid redirect values, we want to clear that
  if (window.location.search.includes('bounceUrl')) {
    window.location.search = '';
  }
};

const App = () => {
  const location = useLocation();
  const { isLoggedIn, isConfirmed, isSubscribed, isOnboarded, onOnboardingComplete } = useApp();
  const { bounceUrl, authRedirect } = useBounceUrl();
  const { logout } = useAuth();
  const isMarketingiFrame = checkMarketingiFrameExists();
  const hasNav = !(
    isMarketingiFrame || ['/login', '/confirmAccount', '/completeSetup'].includes(location.pathname)
  );

  useAnalytics();

  return (
    <>
      <Switch>
        <Route path={['/login', '/confirmAccount', '/completeSetup']} exact />
        <Route path={['/subscribe/success', '/register']} exact>
          <NavBar variant="hideLinks" />
        </Route>
        <Route path={['/preview']} exact>
          {!isMarketingiFrame && <NavBar variant="preview" />}
        </Route>
        <Route>
          <NavBar />
        </Route>
      </Switch>
      <PageLayout hasNav={hasNav}>
        <main id="main-content" style={{ flexGrow: 1, minWidth: 0 }}>
          <Switch>
            <Route exact path="/login">
              {isLoggedIn ? <Redirect to={{ ...location, pathname: '/' }} /> : <Login />}
            </Route>

            <Route path="/subscribe">
              {isSubscribed && isLoggedIn && isConfirmed ? (
                <Redirect to="/settings/subscription" />
              ) : (
                <Subscription />
              )}
            </Route>

            <Route exact path="/confirmAccount" component={ConfirmAccount} />

            <Route exact path="/register" component={Register} />

            <Route path="/completeSetup" component={CompleteSetup} />

            <Route path="/preview">
              {isSubscribed && isLoggedIn && isConfirmed && !isMarketingiFrame ? (
                <Redirect to="/opportunities/all" />
              ) : (
                <PreviewOpportunities />
              )}
            </Route>

            {!isLoggedIn && (
              <Redirect
                to={{
                  ...location,
                  search: bounceUrl,
                  pathname: '/login',
                }}
              />
            )}

            <Route exact path="/unconfirmed" component={Unconfirmed} />

            {/* Must be logged in beyond this point */}
            <Route
              exact
              path="/logout"
              render={() => {
                logout();
                return <Redirect to="/login" />;
              }}
            />

            {!isSubscribed && <Redirect to="/subscribe" />}
            {/* Must be subscribed beyond this point */}

            {!isConfirmed && <Redirect to="/unconfirmed" />}

            <Route exact path="/support" component={Support} />

            <Route exact path="/getStarted" component={GetStarted} />

            <Route exact path="/welcome">
              {isOnboarded ? (
                <Redirect to="/opportunities" />
              ) : (
                <Welcome onOnboardingComplete={onOnboardingComplete} />
              )}
            </Route>

            {!isOnboarded && <Redirect to="/getStarted" />}
            {/* Must be onboarded beyond this point */}
            {authRedirect && <Redirect to={authRedirect} />}
            <Route path="/dashboard" component={Dashboard} />
            <Route path="/opportunities" component={Opportunities} />
            <Route path="/settings" component={Settings} />
            <Route exact path="/" component={TemporaryRootRedirect} />
            <Route path="*" component={NotFound} />
          </Switch>
        </main>
      </PageLayout>
    </>
  );
};

export default () => (
  <ErrorBoundary fallback="Something went wrong." onCatch={onCatch}>
    <AppProviders>
      <Router history={history}>
        <App />
      </Router>
    </AppProviders>
  </ErrorBoundary>
);
